import { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, Send, Check, ChevronDown } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { logError } from "../lib/logger";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const slugify = (s) =>
  (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").substring(0, 40) || "x";

export const CommentsSection = ({ page, itemId, itemTitle }) => {
  const { user } = useAuth();
  const [expanded, setExpanded] = useState(false);
  const [comments, setComments] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [body, setBody] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [justSent, setJustSent] = useState(false);

  // Pre-fill from signed-in user (only when fields are empty)
  useEffect(() => {
    if (user) {
      setName((prev) => prev || user.name || "");
      setEmail((prev) => prev || user.email || "");
    }
  }, [user]);

  const subjectLine = `${page} — ${itemTitle}`;
  const suffix = slugify(itemId);

  const fetchComments = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/comments`, { params: { itemId } });
      setComments(data || []);
    } catch (e) {
      logError("Failed to load comments:", e);
    } finally {
      setLoading(false);
      setLoaded(true);
    }
  };

  useEffect(() => {
    if (expanded && !loaded) fetchComments();
  }, [expanded]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/comments`, {
        page,
        itemId,
        itemTitle,
        authorName: name.trim() || "Guest",
        authorEmail: email.trim() || null,
        body: body.trim(),
      });
      setBody("");
      setJustSent(true);
      setTimeout(() => setJustSent(false), 3000);
      await fetchComments();
    } catch (e) {
      logError("Failed to post comment:", e);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="mt-6 pt-5 border-t border-dashed border-[#D8D3CC]"
      data-testid={`comments-section-${suffix}`}
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="inline-flex items-center gap-2 text-sm text-[#5C5852] hover:text-[#4A6B53] transition-colors"
        data-testid={`comments-toggle-${suffix}`}
      >
        <MessageCircle size={15} strokeWidth={1.7} />
        Comments
        {loaded && comments.length > 0 && (
          <span className="inline-flex items-center justify-center bg-[#CC7A52] text-[#FAF9F6] rounded-full text-[10px] w-5 h-5 ml-0.5">
            {comments.length}
          </span>
        )}
        <ChevronDown
          size={14}
          className={`transition-transform duration-300 ${expanded ? "rotate-180" : "rotate-0"}`}
        />
      </button>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="pt-5">
              <p className="text-[10px] tracking-[0.18em] uppercase text-[#5C5852] font-bold">
                On: <span className="text-[#CC7A52]">{subjectLine}</span>
              </p>

              {loading && (
                <p className="mt-4 text-sm text-[#5C5852] italic">Loading…</p>
              )}
              {!loading && comments.length === 0 && (
                <p className="mt-4 text-sm text-[#5C5852] italic">
                  Be the first to leave a thought.
                </p>
              )}
              {!loading && comments.length > 0 && (
                <ul className="mt-4 space-y-4">
                  {comments.map((c) => (
                    <li
                      key={c.id}
                      className="rounded-sm bg-[#EAE5DC] border border-[#D8D3CC] p-4 sm:p-5"
                      data-testid={`comment-${c.id}`}
                    >
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <span className="font-serif text-base text-[#2C2A28]">
                          {c.authorName}
                        </span>
                        <span className="text-[11px] text-[#5C5852]">
                          {new Date(c.createdAt).toLocaleDateString(undefined, {
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                          })}
                        </span>
                      </div>
                      <p className="mt-2 text-[#2C2A28] leading-relaxed text-sm whitespace-pre-line">
                        {c.body}
                      </p>
                      {c.adminReply && (
                        <div className="mt-3 pt-3 border-t border-dashed border-[#D8D3CC]">
                          <p className="text-[10px] tracking-[0.18em] uppercase text-[#4A6B53] font-bold">
                            ↳ Rajib&apos;s reply
                          </p>
                          <p className="mt-1.5 text-[#2C2A28] leading-relaxed text-sm whitespace-pre-line italic">
                            {c.adminReply}
                          </p>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}

              <form onSubmit={onSubmit} className="mt-5 space-y-3">
                <div className="grid sm:grid-cols-2 gap-3">
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Name (optional, defaults to Guest)"
                    className="bg-[#FAF9F6] border border-[#D8D3CC] rounded-sm px-3 py-2 text-sm text-[#2C2A28] placeholder-[#5C5852]/60 field-ring transition-colors"
                    data-testid={`comment-input-name-${suffix}`}
                  />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Email (optional, not shown publicly)"
                    className="bg-[#FAF9F6] border border-[#D8D3CC] rounded-sm px-3 py-2 text-sm text-[#2C2A28] placeholder-[#5C5852]/60 field-ring transition-colors"
                    data-testid={`comment-input-email-${suffix}`}
                  />
                </div>
                <textarea
                  rows={3}
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  placeholder="Share your thoughts…"
                  className="w-full bg-[#FAF9F6] border border-[#D8D3CC] rounded-sm px-3 py-2 text-sm text-[#2C2A28] placeholder-[#5C5852]/60 field-ring transition-colors resize-y"
                  data-testid={`comment-input-body-${suffix}`}
                />
                <div className="flex items-center justify-end gap-2">
                  {justSent && (
                    <span className="text-xs text-[#4A6B53] inline-flex items-center gap-1">
                      <Check size={12} /> sent
                    </span>
                  )}
                  <button
                    type="submit"
                    disabled={submitting || !body.trim()}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-sm bg-[#4A6B53] text-[#FAF9F6] hover:bg-[#3A5441] transition-colors duration-300 text-xs tracking-wide disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid={`comment-submit-${suffix}`}
                  >
                    {submitting ? "Sending…" : "Post"}
                    {!submitting && <Send size={12} />}
                  </button>
                </div>
              </form>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CommentsSection;
